import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {FormsModule, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {Backend} from '@generated-backend-types';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, filter, of, shareReplay, switchMap, takeUntil, tap} from 'rxjs';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {DateTime, Interval} from 'luxon';
import {MonthPickerComponent} from '../month-picker/month-picker.component';
import {TimeTrackingOverviewService} from '../../../../services/api/time-tracking/time-tracking-overview.service';
import {PublicHolidayService} from '../../../../services/api/time-tracking/public-holiday.service';
import {UserSessionService} from '../../../../services/user-session.service';
import {map} from 'rxjs/operators';
import {DateRowComponent} from './date-row/date-row.component';
import {FormatDurationPipe} from '../../../../pipes/format-duration.pipe';
import {findByDate} from '../../../../utils/find-by-date';
import {applyTimeframeQueryParams, TIMEFRAME_MONTH_PARAM, TIMEFRAME_YEAR_PARAM} from '../../../../utils/timeframe';
import {
  TimeTrackingOverviewEntryService
} from '../../../../services/api/time-tracking/time-tracking-overview-entry.service';
import {
  TimeTrackingEmployeeWeeklyWorkingHoursService
} from '../../../../services/api/time-tracking/time-tracking-employee-weekly-working-hours.service';
import {weekDayEnumFromDateTime} from '../../../../utils/week-day-enum-from-date-time';
import {selectByValidFrom} from '../../../../utils/select-by-valid-from';
import TimeTrackingOverviewByMonth = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingOverview.TimeTrackingOverviewByMonth;
import PublicHoliday = Backend.App.Resources.Dashboard.TimeTracking.PublicHoliday;
import TimeTrackingEmployee = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployee;
import TimeTrackingOverviewEntry = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingOverview.TimeTrackingOverviewEntry;
import TimeTrackingEntryTypeEnum = Backend.App.Enums.TimeTracking.TimeTrackingEntryTypeEnum;
import TimeTrackingEmployeeWeeklyWorkingHours = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployeeWeeklyWorkingHours;
import {ToastrService} from 'ngx-toastr';
import {DecimalPipe} from '@angular/common';

/** Precomputed per-day view model so the template only binds fields */
interface DayRow {
  day: DateTime;
  entry?: TimeTrackingOverviewEntry;
  holiday?: PublicHoliday;
  canBeEdited: boolean;
  shouldWork: boolean;
}

@Component({
  selector: 'app-authenticated-time-tracking-overview-edit',
  templateUrl: './edit.component.html',
  imports: [FormsModule, ReactiveFormsModule, MonthPickerComponent, DateRowComponent, FormatDurationPipe, TranslatePipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public timeTrackingEmployee?: TimeTrackingEmployee;
  public timeTrackingOverview?: TimeTrackingOverviewByMonth;

  public editable = false;

  public days: DateTime[] = [];
  public rows: DayRow[] = [];
  public holidays: PublicHoliday[] = [];
  public entries: TimeTrackingOverviewEntry[] = [];
  private weeklyWorkingHoursHistory: TimeTrackingEmployeeWeeklyWorkingHours[] = [];
  // Identifies the currently selected timeframe; stale async responses (rapid month
  // switching) are discarded when their key no longer matches.
  private currentTimeframeKey = '';
  public timeframeForm: StrictFormGroup<{ year: string, month: string }> = this.fb.group({
    year: this.fb.control(''),
    month: this.fb.control(''),
  });

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    protected readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly router: Router,
    private readonly timeTrackingOverviewService: TimeTrackingOverviewService,
    private readonly publicHolidayService: PublicHolidayService,
    private readonly timeTrackingOverviewEntryService: TimeTrackingOverviewEntryService,
    private readonly weeklyWorkingHoursService: TimeTrackingEmployeeWeeklyWorkingHoursService,
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  private get queryParams() {
    return applyTimeframeQueryParams(
      this.activatedRoute.snapshot.queryParams,
      {year: this.timeframeForm.controls.year.value, month: this.timeframeForm.controls.month.value},
    );
  }

  public canBeEdited(day: DateTime): boolean {
    if (!this.editable) return false;

    if (this.getEntry(day)?.type === TimeTrackingEntryTypeEnum.VACATION) return false;

    return this.canWork(day);
  }

  public ngOnInit() {
    const employee$ = this.activatedRoute.data.pipe(
      switchMap(data => {
        if (data['timeTrackingEmployee']) {
          this.titleService.setTitle('authenticated.time_tracking.overview.edit.title');
          return of(data['timeTrackingEmployee'] as TimeTrackingEmployee);
        }

        this.titleService.setTitle('authenticated.time_tracking.entries.edit.title');

        this.editable = true;
        return this.userSessionService.user$.pipe(map(user => user?.time_tracking_employee));
      }),
      filter((employee): employee is TimeTrackingEmployee => !!employee),
      tap(employee => {
        this.timeTrackingEmployee = employee;
        // Drop the previous employee's history so shouldWork/canWork fall back to the
        // new employee's current_weekly_working_hours until the fresh history arrives.
        this.weeklyWorkingHoursHistory = [];
      }),
      shareReplay({bufferSize: 1, refCount: true}),
    );

    // Working-hours history: switchMap cancels the in-flight request when the employee
    // changes, so a slow/stale response can never be applied to a different employee.
    employee$.pipe(
      switchMap(employee => {
        this.weeklyWorkingHoursService.forEmployee(employee);
        return this.weeklyWorkingHoursService.loadAllPages().pipe(
          // Self-service users may not be allowed to list the sub-resource; fall back to
          // the employee's current_weekly_working_hours instead of breaking the view.
          catchError(err => {
            this.toastrService.error(err.error.message);
            return of([] as TimeTrackingEmployeeWeeklyWorkingHours[]);
          }),
        );
      }),
      takeUntil(this.onDestroy$),
    ).subscribe(history => {
      this.weeklyWorkingHoursHistory = history;
      this.buildRows();
      this.cdr.markForCheck();
    });

    // Query params (year/month) are the single source of truth for what is loaded.
    employee$.pipe(
      switchMap(() => this.activatedRoute.queryParams),
      takeUntil(this.onDestroy$),
    ).subscribe(queryParams => {
      const year = queryParams[TIMEFRAME_YEAR_PARAM] ?? DateTime.now().year.toString();
      const month = queryParams[TIMEFRAME_MONTH_PARAM] ?? DateTime.now().month.toString();

      // emitEvent: false prevents this patch from re-triggering valueChanges -> navigate -> reload.
      this.timeframeForm.patchValue({month, year}, {emitEvent: false});

      const {start, end} = this.monthBounds(Number.parseInt(year), Number.parseInt(month));
      this.days = Interval.fromDateTimes(start, end).splitBy({days: 1})
        .map(day => day.start)
        .filter((day): day is DateTime => day !== undefined && day !== null);
      this.buildRows();

      this.currentTimeframeKey = this.timeframeKey(year, month);
      this.loadEntries(start, end, this.currentTimeframeKey);
      this.loadHolidays(start, end, this.currentTimeframeKey);
      this.loadOverview(this.currentTimeframeKey);
    });

    // User-driven timeframe changes only update the URL; the query-params stream above reacts.
    this.timeframeForm.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.router.navigate([], {queryParams: this.queryParams}).then();
    });
  }

  public getHoliday(date: DateTime): PublicHoliday | undefined {
    return findByDate(this.holidays, date);
  }

  public getEntry(date: DateTime): TimeTrackingOverviewEntry | undefined {
    return findByDate(this.entries, date);
  }

  public shouldWork(day: DateTime): boolean {
    return this.hasWorkDay(day, 'agreed_work_days');
  }

  public canWork(day: DateTime): boolean {
    return this.hasWorkDay(day, 'possible_work_days');
  }

  private hasWorkDay(day: DateTime, kind: 'agreed_work_days' | 'possible_work_days'): boolean {
    const workingHours = this.weeklyWorkingHoursForDay(day);
    if (!workingHours) return false;

    return workingHours[kind].includes(weekDayEnumFromDateTime(day));
  }

  private weeklyWorkingHoursForDay(day: DateTime): TimeTrackingEmployeeWeeklyWorkingHours | undefined {
    // Once the history is loaded it is the source of truth: a day before the earliest
    // valid_from has no applicable config and must stay non-workable (undefined). Only
    // fall back to the current config when no history could be loaded (empty / no permission).
    if (this.weeklyWorkingHoursHistory.length > 0) {
      return selectByValidFrom(this.weeklyWorkingHoursHistory, day);
    }

    return this.timeTrackingEmployee?.current_weekly_working_hours;
  }

  /** Rebuilds the per-day view model; call after days/entries/holidays/working-hours change. */
  private buildRows() {
    this.rows = this.days.map(day => ({
      day,
      entry: this.getEntry(day),
      holiday: this.getHoliday(day),
      canBeEdited: this.canBeEdited(day),
      shouldWork: this.shouldWork(day),
    }));
  }

  private monthBounds(year: number, month: number): { start: DateTime; end: DateTime } {
    const start = DateTime.fromObject({year, month}).startOf('month');

    return {start, end: start.endOf('month')};
  }

  private timeframeKey(year: string | number, month: string | number): string {
    return `${this.timeTrackingEmployee?.id}:${Number(year)}:${Number(month)}`;
  }

  public loadData() {
    const year = Number.parseInt(this.timeframeForm.controls.year.value);
    const month = Number.parseInt(this.timeframeForm.controls.month.value);

    const {start, end} = this.monthBounds(year, month);

    this.currentTimeframeKey = this.timeframeKey(year, month);
    this.loadOverview(this.currentTimeframeKey);
    this.loadEntries(start, end, this.currentTimeframeKey);
  }

  private loadEntries(start: DateTime, end: DateTime, key: string) {
    this.timeTrackingOverviewEntryService.loadAllPages({
      'filter[date]': this.getTimeFrameFilterValue(start, end),
      'filter[time_tracking_employee_id]': this.timeTrackingEmployee!.id,
    }, true).subscribe({
      next: entries => {
        if (key !== this.currentTimeframeKey) return;

        this.entries = entries;

        this.buildRows();
        this.cdr.markForCheck();
      },
      error: err => this.toastrService.error(err.error.message),
    });
  }

  private loadHolidays(start: DateTime, end: DateTime, key: string) {
    const employeeFederalState = this.timeTrackingEmployee?.federal_state;
    if (!employeeFederalState) {
      if (key !== this.currentTimeframeKey) return;

      this.holidays = [];
      this.buildRows();
      this.cdr.markForCheck();
      return;
    }

    this.publicHolidayService.loadAllPages({'filter[date]': this.getTimeFrameFilterValue(start, end)})
      .subscribe(holidays => {
        if (key !== this.currentTimeframeKey) return;

        this.holidays = holidays.filter(holiday => holiday.federal_states.includes(employeeFederalState));
        this.buildRows();
        this.cdr.markForCheck();
      });
  }

  private getTimeFrameFilterValue(start: DateTime, end: DateTime): string {
    return `${start.toISO({suppressMilliseconds: true})},${end.set({millisecond: 0}).toISO({suppressMilliseconds: true})}`;
  }

  private loadOverview(key: string) {
    if (!this.timeTrackingEmployee) return;

    // Spread query params first so a URL-supplied filter[time_tracking_employee_id]
    // can never override the current employee's id (must always win).
    this.timeTrackingOverviewService.loadAllPages({...this.queryParams, 'filter[time_tracking_employee_id]': this.timeTrackingEmployee.id}, true)
      .subscribe({
        next: overviews => {
          if (key !== this.currentTimeframeKey) return;

          let fallbackOverview: TimeTrackingOverviewByMonth | undefined;
          let overview: TimeTrackingOverviewByMonth | undefined;
          overviews.forEach(o => {
            if (o.month === null) {
              fallbackOverview = o;
            } else {
              overview = o;
            }
          });

          this.timeTrackingOverview = overview ?? fallbackOverview;
          this.cdr.markForCheck();
        },
        error: err => this.toastrService.error(err.error.message),
      });
  }
}
